import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, Check, Loader2, RotateCcw, Smartphone, X } from 'lucide-react';
import {
  guideRect,
  isPortrait,
  viewRectToVideo,
  type Rect,
} from '../mapCameraGeometry';

/**
 * In-app camera for BMP map photos (Jim 2026-10-14: maps were coming in crooked,
 * off-centre and sideways). The phone must be held sideways, the map is lined up in a
 * landscape frame, the photo is cropped to that frame, and the inspector approves it or
 * retakes it. Several sheets can be taken in a row. If the browser cannot open the
 * camera, `onUnavailable` hands control back to the phone's own camera.
 */
type Props = {
  onDone: (files: File[]) => void;
  onCancel: () => void;
  onUnavailable: () => void;
};

export function MapCamera({ onDone, onCancel, onUnavailable }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const viewRef = useRef<HTMLDivElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [ready, setReady] = useState(false);
  const [view, setView] = useState({ w: 0, h: 0 });
  const [preview, setPreview] = useState<{ url: string; file: File } | null>(
    null
  );
  const [taken, setTaken] = useState<File[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia)
          throw new Error('no camera api');
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 3840 },
            height: { ideal: 2160 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => undefined);
        }
        setReady(true);
        // best effort: Android can lock landscape inside fullscreen; iOS ignores it
        const orientation = screen.orientation as ScreenOrientation & {
          lock?: (o: string) => Promise<void>;
        };
        orientation?.lock?.('landscape').catch(() => undefined);
      } catch {
        if (!cancelled) onUnavailable();
      }
    })();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, [onUnavailable]);

  useEffect(() => {
    const measure = () => {
      const el = viewRef.current;
      if (el) setView({ w: el.clientWidth, h: el.clientHeight });
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, []);

  const upright = view.w > 0 && isPortrait(view.w, view.h);
  const guide: Rect | null =
    view.w > 0 && !upright ? guideRect(view.w, view.h) : null;

  const shoot = useCallback(async () => {
    const video = videoRef.current;
    if (!video || !guide || !video.videoWidth) return;
    const crop = viewRectToVideo(
      guide,
      view.w,
      view.h,
      video.videoWidth,
      video.videoHeight
    );
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(crop.w);
    canvas.height = Math.round(crop.h);
    canvas
      .getContext('2d')!
      .drawImage(
        video,
        crop.x,
        crop.y,
        crop.w,
        crop.h,
        0,
        0,
        canvas.width,
        canvas.height
      );
    const blob: Blob | null = await new Promise((res) =>
      canvas.toBlob(res, 'image/jpeg', 0.9)
    );
    if (!blob) return;
    const n = taken.length + 1;
    const file = new File([blob], `bmp-map-sheet-${n}.jpg`, {
      type: 'image/jpeg',
    });
    setPreview({ url: URL.createObjectURL(blob), file });
  }, [guide, view, taken.length]);

  const keep = () => {
    if (!preview) return;
    setTaken((t) => [...t, preview.file]);
    URL.revokeObjectURL(preview.url);
    setPreview(null);
  };
  const retake = () => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  return (
    <div
      className="fixed inset-0 z-[2000] bg-black text-white"
      role="dialog"
      aria-label="BMP map camera"
    >
      <div ref={viewRef} className="absolute inset-0 overflow-hidden">
        <video
          ref={videoRef}
          playsInline
          muted
          className="absolute inset-0 w-full h-full object-cover"
        />

        {guide && !preview && (
          <>
            {/* dim everything outside the frame */}
            <div
              className="absolute pointer-events-none"
              style={{
                left: guide.x,
                top: guide.y,
                width: guide.w,
                height: guide.h,
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
                border: '2px solid rgba(255,255,255,0.9)',
                borderRadius: 6,
              }}
              data-testid="map-camera-guide"
            />
            <p
              className="absolute left-0 right-0 text-center text-sm font-medium"
              style={{ top: Math.max(8, guide.y - 30) }}
            >
              Fit the whole map inside the frame, shooting straight down
            </p>
          </>
        )}

        {upright && !preview && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black/80 p-6 text-center">
            <Smartphone className="w-14 h-14 rotate-90" />
            <p className="text-lg font-semibold">Turn your phone sideways</p>
            <p className="text-sm text-white/80">
              BMP maps are wider than they are tall. Hold the phone in landscape
              to take the photo.
            </p>
          </div>
        )}

        {!ready && !upright && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin" />
          </div>
        )}

        {preview && (
          <div className="absolute inset-0 flex flex-col bg-black">
            <img
              src={preview.url}
              alt="Map photo, cropped to the frame"
              className="flex-1 min-h-0 object-contain"
            />
            <div className="flex items-center justify-center gap-3 p-3">
              <button
                type="button"
                onClick={retake}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white/15 font-medium"
              >
                <RotateCcw className="w-4 h-4" /> Retake
              </button>
              <button
                type="button"
                onClick={keep}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 font-medium"
              >
                <Check className="w-4 h-4" /> Use photo
              </button>
            </div>
          </div>
        )}
      </div>

      {!preview && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex flex-col items-center gap-4">
          <button
            type="button"
            onClick={shoot}
            disabled={!ready || upright}
            aria-label="Take photo"
            className="w-16 h-16 rounded-full bg-white text-black flex items-center justify-center disabled:opacity-40"
          >
            <Camera className="w-7 h-7" />
          </button>
        </div>
      )}

      <div className="absolute left-3 top-3 flex items-center gap-2">
        <button
          type="button"
          onClick={onCancel}
          aria-label="Close camera"
          className="p-2 rounded-full bg-black/50"
        >
          <X className="w-5 h-5" />
        </button>
        {taken.length > 0 && !preview && (
          <button
            type="button"
            onClick={() => onDone(taken)}
            className="px-3 py-2 rounded-lg bg-emerald-600 text-sm font-semibold"
          >
            Done · {taken.length} sheet{taken.length === 1 ? '' : 's'}
          </button>
        )}
      </div>
    </div>
  );
}
